import { observer } from 'mobx-react';
import { Button, message, Upload } from 'antd';
import store from '@/store';
import { useScreenLoading } from '@/components/ScreenLoading';
import { $fm, $t } from '@/Intl';
import { removeRanNum } from '@/utils';
import * as API from '@/api';
import { THIRD_APP_ICON_CACHE } from '@/utils/THIRD_APP_ICON_CACHE';
import { importAdaptor } from '@/utils/adaptor';

export default observer(() => {
  const { setScreenLoading } = useScreenLoading();
  const product = store.product.current;

  /**
   * 下载文件并存在store中
   */
  const getTmpFiles = (dir: string, params: ConfigParams) => {
    /**
     * 屏保图片
     */
    const getLocalImgPromise = async () => {
      const localImg = params.screensaver?.localImg;
      if (!localImg) return undefined;
      const _localImg = removeRanNum(localImg);
      params.screensaver!.localImg = _localImg;
      const file = await API.getFile(
        `/.uploads/${dir}/self_define_folder/${localImg}`,
        _localImg,
      );
      store.files.setLocalImg(file);
    };

    /**
     * 自定义语言
     */
    const getCustomLangPromise = async () => {
      const langFile = params.language?.langFile;
      if (!langFile) return undefined;
      const _langFile = removeRanNum(langFile);
      params.language!.langFile = _langFile;
      const file = await API.getFile(
        `/.uploads/${dir}/language/${langFile}`,
        _langFile,
      );
      store.files.setCustomLang(file);
    };

    /**
     * 自定义输入法
     */
    const getCustomInputPromise = async () => {
      const custInput = params.language?.custInputMethodName;
      if (!custInput) return undefined;
      const _custInput = removeRanNum(custInput);
      params.language!.custInputMethodName = _custInput;
      const file = await API.getFile(
        `/.uploads/${dir}/input_method/${custInput}`,
        _custInput,
      );
      store.files.setCustomInput(file);
    };

    /**
     * 背景图片
     */
    const getBgImgPromise = async () => {
      const wallpaperName = params.background?.wallpaperName;
      if (!wallpaperName) return undefined;
      const file = await API.getFile(
        `/.uploads/${dir}/wallpaper/${wallpaperName}`,
        wallpaperName,
      );
      delete params.background!.wallpaperName;
      store.files.setBgImg(file);
    };

    /**
     * 三方app
     */
    const getThirdApp = async () => {
      const thirdApp = params.menu?.thirdApp;
      if (!thirdApp?.length) return undefined;
      const files: File[] = await Promise.all(
        thirdApp.map(async ({ apkName }, i) => {
          const _apkName = removeRanNum(apkName);
          const file = await API.getFile(
            `/.uploads/${dir}/apk/${apkName}`,
            _apkName,
          );
          thirdApp[i].apkName = _apkName;
          return file;
        }),
      );
      store.files.setThirdApp(files);
    };

    return Promise.all([
      getLocalImgPromise(),
      getCustomLangPromise(),
      getCustomInputPromise(),
      getBgImgPromise(),
      getThirdApp(),
    ]);
  };

  /**
   * 上传cust
   */
  const handleUpload = async (file: File) => {
    if (!file.name.includes(product)) {
      message.error($t('ERRORCUST'));
      return false;
    }
    try {
      setScreenLoading(true);
      /**
       * 导入
       */
      const res = await API.importCust({ product, custfile: file });

      /**
       * 覆盖三方app图标缓存
       */
      Object.assign(THIRD_APP_ICON_CACHE, res.icons);

      /**
       * 重置file
       */
      store.files.resetFiles();

      /**
       * 下载并缓存背景图，apk等临时文件
       */
      await getTmpFiles(res.dir, res.params);

      importAdaptor(product, res.params);

      store.params.initConfigParams(res.params);

      /**
       * 重载表单
       */
      store.product.updateTs();

      await API.deleteCustTmp(res.dir);

      setScreenLoading(false);
    } catch (e) {
      message.error($t('ERRORCUST'));
    }

    return false;
  };

  return (
    <Upload showUploadList={false} beforeUpload={handleUpload}>
      <Button type='primary'>{$fm('IMPORT_CONFIG')}</Button>
    </Upload>
  );
});
